import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getCategoriesApi, createCategoryApi, updateCategoryApi, deleteCategoryApi } from '../../api/categoryApi';
import AdminModal, { ConfirmModal } from './AdminModal';

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/(^-|-$)/g,'');
const EMPTY = { name:'', slug:'', description:'', image_url:'', sort_order:'0', is_active:true };

export default function CategoriesTab() {
  const [cats, setCats]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]   = useState(null);
  const [editing, setEditing] = useState(EMPTY);
  const [delId, setDelId]   = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    getCategoriesApi().then((r) => setCats(r.data)).catch(() => toast.error('Failed to load categories')).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openAdd  = () => { setEditing(EMPTY); setModal('add'); };
  const openEdit = (c) => {
    setEditing({ name: c.name, slug: c.slug, description: c.description || '', image_url: c.image_url || '', sort_order: c.sort_order || 0, is_active: c.is_active, _id: c.id });
    setModal('edit');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (modal === 'add') {
        await createCategoryApi({ ...editing, sort_order: parseInt(editing.sort_order) });
        toast.success('Category created!');
      } else {
        await updateCategoryApi(editing._id, { ...editing, sort_order: parseInt(editing.sort_order) });
        toast.success('Category updated!');
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await deleteCategoryApi(delId);
      toast.success('Category deleted');
      setDelId(null);
      load();
    } catch { toast.error('Delete failed — may have linked products'); }
  };

  return (
    <div>
      <div style={S.hdr}>
        <div>
          <h1 style={S.title}>Categories</h1>
          <p style={S.sub}>{cats.length} categories</p>
        </div>
        <button style={S.addBtn} onClick={openAdd}>+ Add Category</button>
      </div>

      <div style={S.grid}>
        {loading
          ? <p style={{ color:'#aaa', textAlign:'center', gridColumn:'1/-1', padding:'40px' }}>Loading...</p>
          : cats.map((c) => (
            <div key={c.id} style={S.card}>
              <div style={S.cardImg}>
                {c.image_url
                  ? <img src={c.image_url} alt={c.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={(e) => { e.target.style.display='none'; }} />
                  : <span style={{ fontSize:'32px' }}>📂</span>
                }
                <span style={{ ...S.activePill, background: c.is_active ? '#DCFCE7' : '#FEE2E2', color: c.is_active ? '#16A34A' : '#DC2626' }}>
                  {c.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div style={S.cardBody}>
                <p style={S.cardName}>{c.name}</p>
                <p style={S.cardSlug}>/{c.slug}</p>
                {c.description && <p style={S.cardDesc}>{c.description.slice(0, 60)}{c.description.length > 60 ? '…' : ''}</p>}
                <div style={S.cardActions}>
                  <button style={S.editBtn} onClick={() => openEdit(c)}>✏️ Edit</button>
                  <button style={S.delBtn} onClick={() => setDelId(c.id)}>🗑️</button>
                </div>
              </div>
            </div>
          ))
        }
        {/* Add card shortcut */}
        <div style={S.addCard} onClick={openAdd}>
          <span style={{ fontSize:'32px', color:'#D1D5DB' }}>+</span>
          <p style={{ fontSize:'13px', color:'#9CA3AF', marginTop:'8px' }}>New Category</p>
        </div>
      </div>

      {/* Modal */}
      {modal && (
        <AdminModal title={modal === 'add' ? 'ADD CATEGORY' : 'EDIT CATEGORY'} onClose={() => setModal(null)} width="480px">
          <form onSubmit={handleSave}>
            <label style={S.lbl}>Category Name *</label>
            <input style={S.inp} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value, slug: slugify(e.target.value) })} required />

            <label style={S.lbl}>Slug *</label>
            <input style={S.inp} value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} required />

            <label style={S.lbl}>Description</label>
            <textarea style={{ ...S.inp, height: '80px', resize: 'vertical' }} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />

            <label style={S.lbl}>Image URL</label>
            <input style={S.inp} placeholder="https://..." value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} />
            {editing.image_url && <img src={editing.image_url} alt="" style={{ height: '60px', marginTop: '6px', objectFit: 'cover' }} onError={(e) => e.target.style.display = 'none'} />}

            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={S.lbl}>Sort Order</label>
                <input style={S.inp} type="number" min="0" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })} />
              </div>
              <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', paddingBottom: '1px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', paddingBottom: '10px' }}>
                  <input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
                  <span style={{ fontSize: '13px', color: '#374151' }}>Active</span>
                </label>
              </div>
            </div>

            <div style={S.formActions}>
              <button type="button" style={S.cancelBtn} onClick={() => setModal(null)}>Cancel</button>
              <button type="submit" style={S.saveBtn} disabled={saving}>{saving ? 'Saving...' : modal === 'add' ? 'Create' : 'Save Changes'}</button>
            </div>
          </form>
        </AdminModal>
      )}

      {delId && <ConfirmModal message="Delete this category? Products in this category will be unlinked." onConfirm={handleDelete} onCancel={() => setDelId(null)} />}
    </div>
  );
}

const S = {
  hdr:         { display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:'20px' },
  title:       { fontFamily:'var(--font-display)', fontSize:'24px', letterSpacing:'1.5px', color:'#111', margin:0 },
  sub:         { fontSize:'13px', color:'#9CA3AF', marginTop:'2px' },
  addBtn:      { background:'#111', color:'#fff', border:'none', padding:'10px 20px', fontFamily:'var(--font-body)', fontSize:'12px', fontWeight:700, letterSpacing:'1px', cursor:'pointer' },
  grid:        { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:'16px' },
  card:        { background:'#fff', border:'1px solid #E5E5E5', overflow:'hidden', boxShadow:'0 1px 3px rgba(0,0,0,.04)' },
  cardImg:     { height:'120px', background:'#F9FAFB', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', position:'relative' },
  activePill:  { position:'absolute', top:'8px', right:'8px', fontSize:'9px', fontWeight:700, padding:'2px 7px', borderRadius:'99px' },
  cardBody:    { padding:'14px 16px' },
  cardName:    { fontFamily:'var(--font-display)', fontSize:'14px', letterSpacing:'1px', color:'#111', marginBottom:'2px' },
  cardSlug:    { fontSize:'11px', color:'#9CA3AF', marginBottom:'6px', fontFamily:'monospace' },
  cardDesc:    { fontSize:'12px', color:'#6B7280', lineHeight:1.5, marginBottom:'10px' },
  cardActions: { display:'flex', gap:'6px' },
  editBtn:     { flex:1, background:'#F9FAFB', border:'1px solid #E5E5E5', padding:'6px 0', fontSize:'12px', fontWeight:600, cursor:'pointer', fontFamily:'var(--font-body)' },
  delBtn:      { background:'#FEE2E2', border:'none', padding:'6px 10px', fontSize:'13px', cursor:'pointer' },
  addCard:     { background:'#FAFAFA', border:'2px dashed #E5E5E5', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'204px', cursor:'pointer', transition:'border-color .2s' },
  lbl:         { display:'block', fontSize:'10px', fontWeight:700, letterSpacing:'1px', textTransform:'uppercase', color:'#6B7280', marginBottom:'5px', marginTop:'14px' },
  inp:         { width:'100%', border:'1.5px solid #E5E5E5', background:'#FAFAFA', padding:'9px 11px', fontFamily:'var(--font-body)', fontSize:'13px', outline:'none', boxSizing:'border-box' },
  formActions: { display:'flex', justifyContent:'flex-end', gap:'10px', marginTop:'20px', paddingTop:'16px', borderTop:'1px solid #F3F4F6' },
  cancelBtn:   { padding:'9px 20px', background:'#fff', border:'1.5px solid #E5E5E5', fontFamily:'var(--font-body)', fontSize:'12px', fontWeight:600, cursor:'pointer', color:'#374151' },
  saveBtn:     { padding:'9px 24px', background:'#111', color:'#fff', border:'none', fontFamily:'var(--font-body)', fontSize:'12px', fontWeight:700, letterSpacing:'1px', cursor:'pointer' },
};
